/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { Case, Switch } from "react-if";
import {
  CostAnalysisChartHeader,
  useCostAnalysis,
  usePeriod,
} from "~/components/Charts/CostAnalysis";
import { ChartType, DateRange } from "~/components/Charts/CostAnalysis/types";
import { getFirstItem } from "~/tools";
import { envChartTypeOptions } from "./types";
import {
  EnvAccumulatedCostView,
  EnvCostPerCategoryView,
  EnvCostVersusBudgetView,
} from "./views";

type Props = {
  envId: string;
  forecast: number;
};

export function EnvironmentCostAnalysisSection({ envId, forecast }: Props) {
  const [range, setRange] = useState<DateRange | undefined>(undefined);

  const {
    period,
    setPeriod,
    granularity,
    setGranularity,
    startDate,
    endDate,
    isDailyChartViewDisabled,
  } = usePeriod(range);

  const { chartType, setChartType, isChartVisible, showCostTable, hasFooter } =
    useCostAnalysis({
      period,
      chartOptions: envChartTypeOptions,
    });

  return (
    <>
      <CostAnalysisChartHeader
        period={period}
        options={envChartTypeOptions}
        chartType={chartType}
        range={range}
        granularity={granularity}
        disableDailyView={isDailyChartViewDisabled}
        onHandleClick={setPeriod}
        onSetRange={setRange}
        onSetGranularity={setGranularity}
        onChange={(item) => {
          setChartType(item ?? getFirstItem(envChartTypeOptions));
        }}
      />
      <Switch>
        <Case condition={isChartVisible(ChartType.ACCUMULATED_COST)}>
          <EnvAccumulatedCostView
            id={envId}
            granularity={granularity}
            startDate={startDate}
            endDate={endDate}
            period={period}
            hasFooter={hasFooter}
            showCostTable={showCostTable}
          />
        </Case>
        <Case condition={isChartVisible(ChartType.COST_VS_BUDGET)}>
          <EnvCostVersusBudgetView
            envId={envId}
            granularity={granularity}
            startDate={startDate}
            endDate={endDate}
            period={period}
            hasFooter={hasFooter}
            showCostTable={showCostTable}
            forecast={forecast}
          />
        </Case>
        <Case condition={isChartVisible(ChartType.COST_PER_CATEGORY)}>
          <EnvCostPerCategoryView
            environmentId={envId}
            granularity={granularity}
            startDate={startDate}
            endDate={endDate}
            period={period}
            hasFooter={hasFooter}
            showCostTable={showCostTable}
          />
        </Case>
      </Switch>
    </>
  );
}
